import { FieldType, Unit, ValueType } from "../../types/enums";
import {
  GroupedProduct,
  ItemAttribute,
  SaleItemInterface,
} from "../../types/types";

// Вспомогательные функции
const getAttributeKey = (attr: ItemAttribute): string => {
  return `${attr.name}|${attr.fieldType}|${attr.valueType}|${attr.unit}`;
};

const parseValue = (
  value: string | string[] | boolean,
  valueType: ValueType,
): string | boolean | string[] => {
  if (valueType === ValueType.NUMBER) {
    const num = parseFloat(value as string);
    return isNaN(num) ? String(value) : num.toString(); // Важно: возвращаем строку
  }
  if (valueType === ValueType.BOOLEAN) {
    return Boolean(value);
  }
  // Для MULTISELECT (строка с запятыми)
  if (
    valueType === ValueType.STRING &&
    typeof value === "string" &&
    value.includes(",")
  ) {
    return value.split(",").map((v) => v.trim());
  }
  return value as string;
};

export const groupProducts = (
  products: SaleItemInterface[],
): (SaleItemInterface | GroupedProduct)[] => {
  // 1. Группируем по groupId
  const groups = new Map<string, SaleItemInterface[]>();

  products.forEach((product) => {
    const key = product.groupId
      ? String(product.groupId)
      : `no-group-${product.id}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(product);
  });

  const result: (SaleItemInterface | GroupedProduct)[] = [];

  // 2. Обрабатываем каждую группу
  for (const [groupId, groupProducts] of groups.entries()) {
    // Если группа с префиксом 'no-group-' или только 1 товар — показываем как отдельные
    if (groupId.startsWith("no-group-") || groupProducts.length === 1) {
      result.push(...groupProducts);
      continue;
    }

    // 3. Анализируем атрибуты внутри группы
    const allAttributes = new Map<string, ItemAttribute>();

    groupProducts.forEach((product) => {
      product.attributes?.forEach((attr) => {
        const key = getAttributeKey(attr);
        allAttributes.set(key, attr);
      });
    });

    // 4. Находим атрибуты, которые есть у ВСЕХ товаров
    const commonAttributeKeys: string[] = [];

    allAttributes.forEach((_, attrKey) => {
      const hasInAll = groupProducts.every((product) =>
        product.attributes?.some((a) => getAttributeKey(a) === attrKey),
      );
      if (hasInAll) {
        commonAttributeKeys.push(attrKey);
      }
    });

    // 5. Разделяем на селекторы и статические атрибуты
    const selectors: Record<string, any> = {};
    const staticAttributes: ItemAttribute[] = [];

    commonAttributeKeys.forEach((attrKey) => {
      const values = groupProducts
        .map((product) => {
          const attr = product.attributes?.find(
            (a) => getAttributeKey(a) === attrKey,
          );
          return attr ? parseValue(attr.value, attr.valueType) : null;
        })
        .filter((v) => v !== null) as (string | boolean | string[])[];

      if (values.length === 0) return;

      const firstValue = values[0];
      const allSame = values.every((v) => {
        if (Array.isArray(v) && Array.isArray(firstValue)) {
          return (
            JSON.stringify([...v].sort()) ===
            JSON.stringify([...firstValue].sort())
          );
        }
        return v === firstValue;
      });

      const sampleAttr = allAttributes.get(attrKey)!;

      if (!allSame && values.length > 1) {
        // Разные значения → селектор
        selectors[attrKey] = {
          name: sampleAttr.name,
          fieldType: sampleAttr.fieldType as FieldType,
          valueType: sampleAttr.valueType as ValueType,
          unit: sampleAttr.unit as Unit,
          values: Array.from(new Set(values)),
        };
      } else {
        // Одинаковые значения → статический атрибут
        staticAttributes.push({
          ...sampleAttr,
          value: firstValue, // Теперь совместимо: string | boolean | string[]
        });
      }
    });

    // 6. Создаём объект группы
    const baseProduct = groupProducts[0];
    const group: GroupedProduct = {
      id: groupId, // 👈 добавь
      type: "group",
      groupId: groupId,
      name: baseProduct.name,
      description: baseProduct.description,
      files: baseProduct.files || [],
      categoryId: baseProduct.categoryId,
      category: baseProduct.category,
      attributes: staticAttributes,
      selectors,
      variants: groupProducts,
      currentVariant: groupProducts[0],
      prices: baseProduct.prices,
    };

    result.push(group);
  }

  return result;
};
